import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ScannerQRCodeConfig, ScannerQRCodeResult } from 'ngx-scanner-qrcode';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

// ANIMATIONS
import { LocalStorageKeys } from 'v2/modules/shared/interfaces/localstorage.interface';

import {
  slideUpAnimation,
  rollDownAnimation,
} from '../../../shared/animations/animation';

// COMPONENTS
import { DialogConfig, SnackBarConfig } from '../../../shared/configs';
import { EScannerType } from '../../../shared/interfaces/common.interface';
import { trackById } from '../../../shared/util/track-by.util';
import { UiWarningDialogComponent } from '../../../ui/components/ui-warning-dialog/ui-warning-dialog.component';

// CONFIGS

// INTERFACES

// SERVICES
import { InventoryApiService } from './../../../shared/services/api/inventory-api.service';
import { LocationApiService } from './../../../shared/services/api/location/location-api.service';
import { RfqApiService } from './../../../shared/services/api/rfq/rfq-api.service';

// UTILS

@Component({
  selector: 'lum-location-warehouse-app-sku-selection',
  templateUrl: './location-warehouse-app-sku-selection.component.html',
  styleUrls: ['./location-warehouse-app-sku-selection.component.scss'],
  animations: [slideUpAnimation, rollDownAnimation],
})
export class LocationWarehouseAppSkuSelectionComponent
  implements OnInit, OnDestroy
{
  rfqs :any[] = []; // TODO: The v1 api still using snake case for the response cannot use IRfq interface
  location :any; // TODO: The v1 api still using snake case for the response cannot use ILocation interface
  loading :boolean = false;
  loadingSku :boolean = false;
  selectedSku :any = null;
  toggleScanner :boolean = false;
  scannerConfig :ScannerQRCodeConfig = {
    fps: 10,
    vibrate: 300,
    isBeep: true,
    constraints: { audio: false, video: true },
  };
  shouldBeep :boolean = true;
  scannedValue :string = '';

  trackById = trackById;

  activeDevice :string = localStorage.getItem(
    LocalStorageKeys.WAREHOUSE_APP_ACTIVE_DEVICE_ID_KEY,
  );

  private onDestroy$ = new Subject();
  relations = [
    'rfqstockLocations:id,location_id,lot_id,onhand_quantity,rfq_stock_id',
    'rfqstockLocations.lot:id,lot_number',
    'rfqstockLocations.rfqStock:id,rfq_id,warehouse_id',
    'rfqstockLocations.rfqStock.rfq:id,internal_sku,product_name,lot_tracking',
    'rfqstockLocations.rfqStock.rfq.activePicklistItems:id,rfq_id,picklist_id,available_quantity',
  ];

  constructor(
    private route :ActivatedRoute,
    private cd :ChangeDetectorRef,
    private locationApiService :LocationApiService,
    private inventoryApiService :InventoryApiService,
    private snackBar :MatSnackBar,
    private router :Router,
    private dialog :MatDialog,
    private rfqApiService :RfqApiService,
  ) {}

  ngOnInit() :void {
    this.getRouterQueryParams().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onInputChangeEvent(sku :string) :void {
    this.loadingSku = true;
    if (!sku) {
      this.selectedSku = null;
    }

    this.inventoryApiService
      .get({
        sku,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          this.loadingSku = false;
          this.rfqs = response.data;
          this.cd.detectChanges();
        },
        error: (error) => {
          this.loadingSku = false;

          return error;
        },
      });
  }

  onSkuSelectedEvent(value :any) :void {
    this.selectedSku = value;
  }

  onScanned(data :any) :void {
    if (data?.type === EScannerType.RFQ) {
      //Return the rfqstock_location that has s.rfq_stock?.rfq?.id == data.id
      const rfqStockLocation = this.location.rfqstock_locations.find(
        (s :any) => s.rfq_stock?.rfq?.id === data.id,
      );
      if (rfqStockLocation) {
        this.navigetToQuantityEntry(rfqStockLocation.id);
      } else {
        this.rfqApiService
          .getById(data.id)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (rfq) => {
              this.selectedSku = rfq;
              this.addSkuToLocation();
            },
            error: (error) => {
              this.snackBar.open(
                `An unknown error occurred adding this sku. We're looking into it!`,
                'Fail',
                {
                  duration: SnackBarConfig.DEFAULT.duration,
                },
              );

              return error;
            },
          });
      }
    } else {
      this.dialog.open(UiWarningDialogComponent, {
        data: {
          title: 'Warning',
          message: `Type: ${data?.type} is not a valid SKU. Please scan a valid SKU QR code to continue.`,
        },
        width: DialogConfig.sizes.small,
      });
    }
  }

  onAddSku() :void {
    if (!this.selectedSku?.stock?.id) {
      this.snackBar.open(
        `An unknown error occurred adding this sku. We're looking into it!`,
        'Fail',
        {
          duration: SnackBarConfig.DEFAULT.duration,
        },
      );

      return;
    }

    if (
      this.location.rfqstock_locations.every(
        (s :any) => s.rfq_stock?.rfq?.id !== this.selectedSku.id,
      )
    ) {
      this.addSkuToLocation();
    }
  }

  navigetToQuantityEntry(rfqStockLocationId :number) :void {
    this.router.navigate([
      '/warehouse-mobile-app/quantity-entry',
      rfqStockLocationId,
    ]);
  }

  private getRouterQueryParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        const id = params['locationId'];
        if (id) {
          this.getLocation(id);
        }
      }),

      takeUntil(this.onDestroy$),
    );
  }

  private getLocation(id :number) :void {
    this.loading = true;
    this.locationApiService
      .getByIdV2({
        id: id,
        params: {
          relations: this.relations.join('|'),
        },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (data) => {
          this.location = data;
          this.loading = false;
          this.cd.detectChanges();
        },
        error: (error) => {
          this.loading = false;
          this.cd.detectChanges();

          return error;
        },
      });
  }

  private addSkuToLocation() :void {
    this.loading = true;
    this.inventoryApiService
      .addStockToLocation({
        stockId: this.selectedSku.stock.id,
        payload: {
          location_ids: [this.location.id],
          warehouse_id: this.location.parent_id,
          rfq_id: this.selectedSku.id,
        },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          this.loading = false;
          const rfqStockLocation = response.stock_locations.find(
            (s :any) => s.pivot.location_id === this.location.id,
          );
          this.navigetToQuantityEntry(rfqStockLocation.pivot.id);

          this.snackBar.open(`Successfully added SKU to Location`, 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.cd.detectChanges();
        },
        error: (error) => {
          this.loading = false;
          this.snackBar.open(
            `An unknown error occurred adding this sku. We're looking into it!`,
            'Fail',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
          this.cd.detectChanges();

          return error;
        },
      });
  }
}
